/* global localStorage */

import React, { forwardRef, startTransition, useCallback, useEffect, useState } from 'react';
import SearchPageProductListItem from './SearchPageProductListItem';
import { array, bool, func, node, number, object, string } from 'prop-types';
import { CgMenuGridR } from 'react-icons/cg';
import { FaList } from 'react-icons/fa';
import PageNavButtons from './PageNavButtons';
import SearchWithin from './SearchWithin';
import NavigationSkeleton from './NavigationSkeleton';
import constants from '../constants.json' assert { type: 'json' };
import { limitSet, median } from './SearchPageSidebar';

/** CONFIGURATION */
/* Sort types */
const sortMap = constants.SEARCH_PAGE.SORT_MAP;
const DUMMY_PRODUCTS_AMOUNT = 8;

const SearchPageProductList = forwardRef(({ isMobile, productList, searchLoading, collectionHandle, children, searchQuery, totalPages, curPage, paginateUser, setSearchParams = () => {} }, ref) => {
    const [view, setView] = useState('grid');
    const [sortType, setSortType] = useState('');
    const [limit, setLimit] = useState('');

    /* Custom sort */
    const handleSetSortType = (e) => {
        e.preventDefault();
        let { value } = e.target;
        const findValueParamInSortMap = Object.values(sortMap)
            .filter((v) => v.param === value);
        if (!findValueParamInSortMap.length) {
            value = sortMap[Object.keys(sortMap)[0]].param;
            return;
        }
        setSortType(value);
        setSearchParams({
            ...searchQuery,
            sortBy: value,
        });
    };

    /* End custom sort */
    /* Custom limit */
    const handleSetLimit = (e) => {
        e.preventDefault();
        let { value } = e.target;
        value = parseInt(value);
        if (isNaN(value) || value < Math.min(limitSet) || value > Math.max(limitSet)) {
            value = median(limitSet);
        }
        setLimit(value);
        setSearchParams({
            ...searchQuery,
            limit: parseInt(value),
        });
    };


    const onViewChange = useCallback((view) => {
        setView(view);

        if (typeof window !== 'undefined') {
            localStorage.setItem('view', view);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const view = localStorage.getItem('view');

        if (view) {
            startTransition(() => {
                setView(view);
            });
        }
    }, []);

    return (<ul
        ref={ref}
        className={'search-page--product-list'}>
        <div
            className={'search-page--header-container'}
        >
            <div
                className={'d-flex flex-wrap gap-3 align-items-center mb-2'}>
                {!searchLoading && (<div
                    className={'d-flex align-items-center gap-2 text-nowrap'}>

                    <span>View as: </span>
                    <button
                        className={`search-page-icon-wrapper ${view === 'grid' ? 'active-view' : ''} rounded-circle`}
                        onClick={() => onViewChange('grid')}
                        style={{ padding: '.25em' }}
                    >
                        <CgMenuGridR
                            fontSize={'32px'}
                        />
                    </button>
                    <button
                        className={`search-page-icon-wrapper ${view === 'list' ? 'active-view' : ''} rounded-circle`}
                        onClick={() => onViewChange('list')}
                    >
                        <FaList
                            fontSize={'27px'}
                        />
                    </button>
                </div>)}
                {!searchLoading && <div
                    style={{
                        minWidth: '12em',
                    }}>
                    <SearchWithin />
                </div>}
                {!searchLoading && <div
                    className={'d-flex align-items-center gap-1 text-nowrap'}
                    style={{
                        minWidth: '12em',
                    }}
                >
                    <span>Sort by:</span>
                    <select
                        className={'form-select form-select--small search-sidebar--input'}
                        name={'Sort by'}
                        value={sortType}
                        onChange={handleSetSortType}>
                        {Object.entries(sortMap).map(([k, v]) => (
                            <option
                                key={k}
                                value={v.param}>{v.text}</option>
                        ))}
                    </select>
                </div>}

                {!searchLoading && <div
                    style={{
                        minWidth: '6em',
                    }}
                >
                    <select
                        className={'form-select form-select--small search-sidebar--input'}
                        name={'Limit'}
                        value={limit}
                        onChange={handleSetLimit}>
                        <option
                            value={''}>Default</option>
                        {limitSet.map((l) => (
                            <option
                                key={l}
                                value={l}>{l} per page</option>
                        ))}
                    </select>
                </div>}
                {searchLoading && <NavigationSkeleton />}
            </div>
            {!searchLoading && (<div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexFlow: 'row nowrap',
                    justifyContent: 'space-between',
                }}>
                {children?.props?.children[0]}
                <PageNavButtons
                    curPage={curPage}
                    totalPages={totalPages}
                    paginateUser={paginateUser}
                />
            </div>)}
        </div>
        {(searchLoading === false && !productList.length) && (<p
            style={{ height: '24px' }}>No products match your search criteria, please try again.</p>)}
        <div
            className={`search-page--product-list-inner ${view}`}>
            {(productList.length > 0 && searchLoading === false) && productList.map((product, i) => (
                <SearchPageProductListItem
                    index={i}
                    key={product.id}
                    isMobile={isMobile}
                    title={product.title}
                    handle={product.handle}
                    imageUrl={product.imageUrl}
                    price={product.price}
                    compareAtPrice={product.compareAtPrice}
                    availableForSale={product.availableForSale}
                    avgRating={product.avgRating}
                    totalReviews={product.totalReviews}
                    searchLoading={searchLoading}
                    collectionHandle={collectionHandle}
                    isFeatured={product.isFeatured}
                    inventoryLevelId={product?.availableInventoryLevels?.at(0)?.id ?? 0}
                    availableQuantity={product?.availableInventoryLevels?.at(0)?.availableQuantity ?? 0}
                    bypassNegativeQuantity={product?.availableInventoryLevels?.at(0)?.bypassNegativeQuantity ?? false}
                    view={view}
                    vendor={product.vendor}
                />
            ))}
            {searchLoading === true && new Array(DUMMY_PRODUCTS_AMOUNT).fill(0).map((item, idx) => (
                <SearchPageProductListItem
                    index={idx}
                    key={`${item}_${idx}`}
                    isMobile={isMobile}
                    searchLoading={searchLoading} />
            ))}
        </div>
        {!searchLoading && productList.length > 0 ? (<div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
            }}>
            <div
                className={'mx-auto'}>
                {children?.props?.children[1]}
            </div>
            <PageNavButtons
                curPage={curPage}
                totalPages={totalPages}
                paginateUser={paginateUser}
            />
        </div>) : null}
    </ul>);
});

SearchPageProductList.displayName = 'SearchPageProductList';

SearchPageProductList.propTypes = {
    isMobile: bool,
    productList: array,
    searchLoading: bool,
    collectionHandle: string,
    children: node,
    searchQuery: object,
    totalPages: number,
    curPage: number,
    paginateUser: func,
    setSearchParams: func,
};

SearchPageProductList.defaultProps = {
    collectionHandle: '',
};

export default SearchPageProductList;